import { SvgSpinners3DotsBounceIcon } from "@/components/icons/svg-spinners-3-dots-bounce";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import type { ChatAttachment } from "@/features/attachments/attachments.types";
import { FileIconHelper } from "@/features/attachments/components/file-icon-helper";
import { useDeleteMessageMutation, useUpdateMessageMutation } from "@/features/chat/api/chat-hooks";
import { KEY_ENTER, KEY_ESCAPE } from "@/lib/keyboard-keys";
import { cn } from "@/lib/utils";
import { plural } from "@/lib/wuchale";
import type { ChatMessageWithAuthorView } from "@shared/types/supabase.types";
import { Check, MoreVertical, Pencil, Square, Trash2, X } from "lucide-react";
import { useRef, useState } from "react";
import type { StreamingStep } from "../api/chat-ai";
import { findAskUserAnswer, formatFileSize } from "../chat.utils";
import { AiMessageContent } from "./ai-message-content";

/**
 * CR-172: the persisted "Response stopped" row of a stopped AI turn — the
 * api-server writes `metadata.stop_reason = "cancelled"` (service role only;
 * the messages trigger nulls metadata from any other writer).
 */
// @wc-ignore
const CANCELLED_STOP_REASON = "cancelled";

function isCancelledReply(message: ChatMessageWithAuthorView): boolean {
  const metadata = message.metadata;
  return (
    !!metadata &&
    typeof metadata === "object" &&
    !Array.isArray(metadata) &&
    metadata.stop_reason === CANCELLED_STOP_REASON
  );
}

/** CR-91: the live tool-step list shows only the most recent steps. */
const MAX_VISIBLE_STEPS = 10;

interface ChatMessageItemProps {
  message: ChatMessageWithAuthorView;
  isOwnMessage: boolean;
  showHeader: boolean;
  attachments: ChatAttachment[];
  messages: ChatMessageWithAuthorView[];
  messageIndex: number;
  onSubmitAskUserAnswer?: (answer: string) => Promise<void>;
}

export const ChatMessageItem = ({
  message,
  isOwnMessage,
  showHeader,
  attachments,
  messages,
  messageIndex,
  onSubmitAskUserAnswer,
}: ChatMessageItemProps) => {
  const messageContent = message.content ?? "";
  const streamingMeta = message as ChatMessageWithAuthorView & {
    steps?: StreamingStep[];
    reasoning?: string;
    thinking?: boolean;
    streaming?: boolean;
    stopped?: boolean;
  };
  const steps = streamingMeta.steps;
  // CR-91: tools run sequentially, so the running step is always the newest and never trimmed.
  const visibleSteps = steps?.slice(-MAX_VISIBLE_STEPS) ?? [];
  const trimmedStepCount = (steps?.length ?? 0) - visibleSteps.length;
  const reasoning = streamingMeta.reasoning?.trim() ?? "";
  const isThinking = streamingMeta.thinking === true;
  const isStreaming = streamingMeta.streaming === true;

  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(messageContent);
  const inputRef = useRef<HTMLInputElement>(null);

  const updateMutation = useUpdateMessageMutation();
  const deleteMutation = useDeleteMessageMutation();

  const displayName =
    (message.first_name || message.last_name
      ? `${message.first_name ?? ""} ${message.last_name ?? ""}`.trim()
      : "") ||
    message.full_name?.trim() ||
    message.email ||
    "";

  const isAiMessage = message.actor_type === "ai";

  const askUserAnswerContent = findAskUserAnswer(messageIndex, messages);

  // CR-172: checked BEFORE the streaming placeholder — an empty local stopped
  // bubble must never read "Thinking…". The stored English text of the
  // persisted placeholder is not shown: the label below is translated.
  const isStoppedReply =
    isAiMessage && (streamingMeta.stopped === true || isCancelledReply(message));

  const isStreamingPlaceholder = message.actor_type === "ai" && !message.content?.trim();

  const canModify = isOwnMessage && !isAiMessage && !!message.id && !!message.room;

  const handleStartEdit = () => {
    setEditContent(messageContent);
    setIsEditing(true);
  };

  const handleSaveEdit = () => {
    const trimmed = editContent.trim();
    if (!message.id || !message.room) return;

    if (!trimmed || trimmed === messageContent) {
      setIsEditing(false);
      return;
    }

    updateMutation.mutate(
      { messageId: message.id, content: trimmed, room: message.room },
      { onSuccess: () => setIsEditing(false) },
    );
  };

  const handleCancelEdit = () => {
    setEditContent(messageContent);
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (!message.id || !message.room) return;
    deleteMutation.mutate({ messageId: message.id, room: message.room });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KEY_ENTER) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === KEY_ESCAPE) {
      handleCancelEdit();
    }
  };

  return (
    <div className={cn("group mt-2 flex", isOwnMessage ? "justify-end" : "justify-start")}>
      <div
        className={cn("flex w-fit max-w-[75%] flex-col gap-1", {
          "items-end": isOwnMessage,
        })}
      >
        {!!showHeader && (
          <div
            className={cn("flex items-center gap-2 px-3 text-xs", {
              "flex-row-reverse justify-end": isOwnMessage,
            })}
          >
            <span className={"font-medium"}>{displayName}</span>
            {!!message.created_at && (
              <span className="text-foreground/50 text-xs">
                {new Date(message.created_at).toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })}
              </span>
            )}
          </div>
        )}

        {/* Ephemeral tool-step indicators (streaming only) */}
        {steps && steps.length > 0 ? (
          <div className="mb-1 flex flex-col gap-0.5">
            {trimmedStepCount > 0 ? (
              <div
                className="text-foreground/50 text-xs"
                data-testid="earlier-steps"
                data-count={trimmedStepCount}
              >
                {plural(trimmedStepCount, ["1 earlier action", "# earlier actions"])}
              </div>
            ) : null}
            {visibleSteps.map((step, i) => (
              <div
                // Original index keeps row identity while the window slides.
                key={trimmedStepCount + i}
                className="text-foreground/50 flex items-center gap-1.5 text-xs"
                data-testid="tool-step"
              >
                {step.status === "running" ? (
                  <SvgSpinners3DotsBounceIcon size={12} />
                ) : (
                  <Check className="size-3 shrink-0" />
                )}
                <span>{step.label}</span>
              </div>
            ))}
          </div>
        ) : null}

        {/* Bubble with top-right actions menu */}
        <div className="relative w-fit">
          {canModify && !isEditing ? (
            <div className="absolute -top-2 -right-2 z-10 opacity-0 transition-opacity group-hover:opacity-100">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon-xs"
                    className="rounded-full"
                    aria-label="Message options"
                  >
                    <MoreVertical className="size-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" side="bottom">
                  <DropdownMenuItem onClick={handleStartEdit}>
                    <Pencil />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : null}

          <div
            className={cn(
              "w-fit rounded-xl px-3 py-2 text-sm",
              isOwnMessage && "bg-bubble-own text-foreground",
              !isOwnMessage && isAiMessage && "bg-bubble-ai text-foreground",
              !isOwnMessage && !isAiMessage && "bg-bubble-other text-foreground",
            )}
          >
            {isStoppedReply ? (
              <div
                className="text-muted-foreground flex items-center gap-1.5 text-xs italic"
                data-testid="ai-response-stopped"
              >
                <Square className="size-3 shrink-0" aria-hidden="true" />
                <span>Response stopped</span>
              </div>
            ) : isStreamingPlaceholder ? (
              reasoning ? (
                // Live, transient reasoning — greyed, italic; replaced by the
                // answer the moment the final response starts streaming.
                <div
                  className="text-foreground/50 flex items-start gap-2 text-xs italic"
                  role="status"
                  aria-live="polite"
                >
                  <SvgSpinners3DotsBounceIcon size={14} className="mt-0.5 shrink-0" />
                  <span className="whitespace-pre-wrap">{reasoning}</span>
                </div>
              ) : (
                <div
                  className="flex items-center justify-center gap-2"
                  role="status"
                  aria-live="polite"
                >
                  <SvgSpinners3DotsBounceIcon size={18} />
                  <span className={isThinking ? "text-foreground/50 text-xs" : "sr-only"}>
                    {isThinking ? "Thinking…" : "AI is typing..."}
                  </span>
                </div>
              )
            ) : isEditing ? (
              <div className="flex min-w-48 flex-col gap-2">
                <Input
                  ref={inputRef}
                  autoFocus
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={updateMutation.isPending}
                />
                <div className="flex justify-end gap-1">
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground rounded-full"
                    onClick={handleCancelEdit}
                    aria-label="Cancel edit"
                  >
                    <X className="size-3.5" />
                  </Button>
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground rounded-full"
                    onClick={handleSaveEdit}
                    disabled={updateMutation.isPending}
                    aria-label="Save edit"
                  >
                    <Check className="size-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {attachments.length > 0 && (
                  <div
                    className={cn(
                      "grid gap-1.5",
                      messageContent.length < 60 ? "max-w-48 grid-cols-1" : "grid-cols-3",
                    )}
                  >
                    {attachments.map((attachment) => {
                      const isUploading = "isUploading" in attachment && attachment.isUploading;

                      return (
                        <div
                          key={attachment.id}
                          className={cn(
                            "bg-background/20 hover:bg-background/30 relative flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs transition-colors",
                            !isOwnMessage && "bg-background/70 hover:bg-background/80",
                            !isUploading && "cursor-pointer",
                          )}
                        >
                          <FileIconHelper
                            mimeType={attachment.mime_type}
                            className="text-current"
                          />
                          <div className="min-w-0">
                            <p className="truncate font-medium">{attachment.file_name}</p>
                            <p className="opacity-75">
                              {isUploading ? "Uploading..." : formatFileSize(attachment.size_bytes)}
                            </p>
                          </div>
                          {!isUploading && (
                            <a
                              href={attachment.signedUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="absolute inset-0"
                            >
                              <span className="sr-only">Open {attachment.file_name}</span>
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {!!messageContent && (
                  <AiMessageContent
                    content={messageContent}
                    isStreaming={isStreaming}
                    askUserAnswered={askUserAnswerContent !== undefined}
                    askUserAnswerContent={askUserAnswerContent}
                    onSubmitAskUserAnswer={onSubmitAskUserAnswer}
                  />
                )}
                {/* Activity indicator while the model is producing held-back
                    content (a doc-list widget / JSON being generated) — keeps
                    the partially-streamed bubble from looking frozen. */}
                {isThinking && messageContent.trim() ? (
                  <div className="text-foreground/50 flex items-center gap-1.5 text-xs italic">
                    <SvgSpinners3DotsBounceIcon size={12} className="shrink-0" />
                    <span>Thinking…</span>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
